/**
 * `mintMissingIds` — stable ids for items lacking them, so translations attach
 * to items the lazy editor never built. Must agree with `h5peditor-group.js`.
 */

import {
  ASSET_EMBED_LIBRARIES,
  EXCLUDED_LIBRARIES,
  FEEDBACK_RANGES_KEY,
  deepCloneJson,
  hasTranslatableFieldWithinGroups,
  isFeedbackRangesGroup,
  isFlattenedGroup,
  isGroupField,
  isKeyableFlattenedItem,
  isLibraryField,
  isListField,
  isRecord,
  parseLibraryValue,
  stableKeyOf,
  toResolverFn,
} from './internal';
import { createUuidV4 } from './uuid';
import {
  H5PParams,
  H5PSemantics,
  H5PSemanticsField,
  MintResult,
  SemanticsResolverFn,
  WalkerOptions,
} from './types';

interface MintContext {
  readonly resolver: SemanticsResolverFn;
  changed: boolean;
}

export const mintMissingIds = (
  semantics: H5PSemantics,
  params: H5PParams,
  options?: WalkerOptions,
): MintResult => {
  const minted = deepCloneJson(params);
  const context: MintContext = {
    resolver: toResolverFn(options?.resolveSemantics),
    changed: false,
  };
  mintFields(semantics, minted, context);
  return { params: minted, changed: context.changed };
};

const mintFields = (
  fields: readonly H5PSemanticsField[],
  container: unknown,
  context: MintContext,
): void => {
  for (const field of fields) {
    if (field.common === true || typeof field.name !== 'string') {
      continue;
    }
    const value = isRecord(container) ? container[field.name] : undefined;
    mintField(field, value, context);
  }
};

const mintField = (
  field: H5PSemanticsField,
  value: unknown,
  context: MintContext,
): void => {
  if (field.common === true) {
    return;
  }
  if (isFeedbackRangesGroup(field)) {
    if (field.translatable === true) {
      mintFeedbackRanges(value, context);
    }
    return;
  }
  if (isGroupField(field)) {
    if (field.isSubContent === true && isRecord(value) && !stableKeyOf(value)) {
      value['subContentId'] = createUuidV4();
      context.changed = true;
    }
    if (isFlattenedGroup(field)) {
      mintField(field.fields?.[0] as H5PSemanticsField, value, context);
      return;
    }
    mintFields(field.fields ?? [], value, context);
    return;
  }
  if (isListField(field)) {
    mintList(field, value, context);
    return;
  }
  if (isLibraryField(field)) {
    mintLibrary(field, value, context);
  }
};

const mintList = (
  field: H5PSemanticsField,
  value: unknown,
  context: MintContext,
): void => {
  const itemDef = field.field;
  if (!itemDef) {
    return;
  }
  // Name-nested list values (imageHotspotQuestion widget) — same unwrap rule
  // as the walker traversal.
  const items = Array.isArray(value)
    ? value
    : isRecord(value) &&
        typeof field.name === 'string' &&
        Array.isArray(value[field.name])
      ? (value[field.name] as unknown[])
      : undefined;
  if (!items) {
    return;
  }
  items.forEach((item, index) => {
    // Legacy bare-string item: wrap into the group shape with a fresh id.
    if (typeof item === 'string' && typeof field.stringItemField === 'string') {
      items[index] = { id: createUuidV4(), [field.stringItemField]: item };
      context.changed = true;
      return;
    }
    if (isGroupField(itemDef) && !isFeedbackRangesGroup(itemDef)) {
      // A flattened item IS its single field's value — only record-valued
      // leaves can hold an id; bare-string items stay unkeyed.
      const keyable = isFlattenedGroup(itemDef)
        ? isKeyableFlattenedItem(itemDef)
        : hasTranslatableFieldWithinGroups(itemDef);
      if (
        isRecord(item) &&
        itemDef.isSubContent !== true &&
        keyable &&
        !stableKeyOf(item)
      ) {
        item['id'] = createUuidV4();
        context.changed = true;
      }
      mintField(itemDef, item, context);
      return;
    }
    if (isLibraryField(itemDef)) {
      mintLibrary(itemDef, item, context);
    }
  });
};

const mintLibrary = (
  field: H5PSemanticsField,
  value: unknown,
  context: MintContext,
): void => {
  const library = parseLibraryValue(value);
  if (!library || EXCLUDED_LIBRARIES.includes(library.machineName)) {
    return;
  }
  // Asset embeds (H5P.Image / H5P.Video) never recurse — their semantics are
  // not needed to mint the editor-invariant subContentId.
  if (ASSET_EMBED_LIBRARIES[library.machineName] !== undefined) {
    if (!library.subContentId && isRecord(value)) {
      value['subContentId'] = createUuidV4();
      context.changed = true;
    }
    return;
  }
  const subSemantics = context.resolver(library.uberName);
  if (!subSemantics) {
    // Unknown embed: leave untouched (we could not walk it either).
    return;
  }
  if (!library.subContentId && isRecord(value)) {
    value['subContentId'] = createUuidV4();
    context.changed = true;
  }
  mintFields(subSemantics, library.params ?? {}, context);
};

const mintFeedbackRanges = (value: unknown, context: MintContext): void => {
  const ranges = isRecord(value) ? value[FEEDBACK_RANGES_KEY] : undefined;
  if (!Array.isArray(ranges)) {
    return;
  }
  for (const range of ranges) {
    if (isRecord(range) && !stableKeyOf(range)) {
      range['id'] = createUuidV4();
      context.changed = true;
    }
  }
};
